import { DateFilter, DateFilterDefinition, getDateFilterDefinitions } from "@superwall/charts"
import { createContext, FC, PropsWithChildren, useReducer } from "react"
import { useQueryState } from "next-usequerystate"

type Reducer<S, A> = (s: S, a: A) => S

type TimeSpan = {
  duration: number
  endRefDate: number
}

type Environment = "PRODUCTION" | "SANDBOX"

type GlobalDataOptions = {
  timeSpan: TimeSpan
  dateFilter: DateFilter
  environment: Environment
  byUser: boolean
}

type GlobalDataOptionsStore = GlobalDataOptions & {
  updateTimeSpan: ({}: { endISODate?: number; duration?: number }) => void
  setDateFilter: (dateFilter: DateFilter) => void
  setEnvironment: (environment: Environment) => void
  setByUser: (byUser: boolean) => void
  parseDatePeriodToTimeSpan: (startISODate: number, endISODate: number) => TimeSpan
  parseURLToTimeSpan: (url: string) => TimeSpan
}

const defaultDataOptions: GlobalDataOptionsStore = {
  dateFilter: getDateFilterDefinitions()[0]!,
  timeSpan: {
    // milliseconds in a day
    duration: 0,
    endRefDate: Date.now(),
  },
  byUser: false,
  environment: "PRODUCTION",
  setDateFilter: () => {},
  updateTimeSpan: () => {},
  setEnvironment: (environment: Environment) => undefined,
  setByUser: (byUser: boolean) => undefined,
  parseURLToTimeSpan: (url: string) => {
    return {
      // milliseconds in a day
      duration: 0,
      endRefDate: Date.now(),
    }
  },
  parseDatePeriodToTimeSpan: (startISODate: number, endISODate: number) => {
    return {
      // milliseconds in a day
      duration: 0,
      endRefDate: Date.now(),
    }
  },
}

type GlobalDataOptionsAction =
  | { key: "setTimeSpan"; endISODate: number; duration: number }
  | { key: "SetByUser"; byUser: boolean }
  | { key: "setTimeSpanDuration"; duration: number }
  | { key: "setTimeSpanEndDate"; endISODate: number }
  | { key: "setDateFilter"; dateFilter: DateFilter }
  | { key: "setEnvironment"; environment: Environment }

const dataOptionsReducer: Reducer<GlobalDataOptions, GlobalDataOptionsAction> = (state, action) => {
  switch (action.key) {
    case "setTimeSpan":
      return {
        ...state,
        timeSpan: {
          endRefDate: action.endISODate,
          duration: action.duration,
        },
      }
    case "setTimeSpanDuration":
      return {
        ...state,
        timeSpan: {
          ...state.timeSpan,
          duration: action.duration,
        },
      }
    case "setTimeSpanEndDate":
      return {
        ...state,
        timeSpan: {
          ...state.timeSpan,
          endRefDate: action.endISODate,
        },
      }
    case "SetByUser":
      return {
        ...state,
        byUser: action.byUser,
      }
    case "setDateFilter":
      return {
        ...state,
        dateFilter: action.dateFilter,
      }
    case "setEnvironment":
      return {
        ...state,
        environment: action.environment,
      }
    default:
      throw new Error()
  }
}

export const GlobalDataOptionsContext = createContext<GlobalDataOptionsStore>(defaultDataOptions)

export const GlobalDataOptions: FC<PropsWithChildren<{}>> = ({ children }) => {
  const [envParam, setEnvParam] = useQueryState("env", {
    parse: (value: string): Environment => {
      return value === "SANDBOX" ? "SANDBOX" : ("PRODUCTION" as Environment)
    },
    serialize: (value: Environment): string => value,
    defaultValue: "PRODUCTION" as Environment,
  })

  const [{ timeSpan, byUser, dateFilter, environment }, dispatch] = useReducer<
    Reducer<GlobalDataOptions, GlobalDataOptionsAction>
  >(dataOptionsReducer, {
    ...defaultDataOptions,
    environment: envParam,
  })

  const updateTimeSpan = ({ endISODate, duration }: { endISODate?: number; duration?: number }) => {
    if (endISODate && !duration) {
      if (endISODate === timeSpan.endRefDate) {
        return
      }

      dispatch({
        key: "setTimeSpanEndDate",
        endISODate,
      })
    } else if (duration && !endISODate) {
      if (duration === timeSpan.duration) {
        return
      }
      dispatch({
        key: "setTimeSpanDuration",
        duration,
      })
    } else if (duration && endISODate) {
      if (duration === timeSpan.duration && endISODate === timeSpan.endRefDate) {
        return
      }
      dispatch({
        key: "setTimeSpan",
        duration,
        endISODate,
      })
    }
  }

  const setDateFilter = (dateFilter: DateFilterDefinition) => {
    dispatch({
      key: "setDateFilter",
      dateFilter,
    })
  }

  const setByUser = (byUser: boolean) => {
    dispatch({
      key: "SetByUser",
      byUser,
    })
  }

  const setEnvironment = (newEnv: Environment) => {
    setEnvParam(newEnv)
    dispatch({
      key: "setEnvironment",
      environment: newEnv,
    })
  }

  return (
    <GlobalDataOptionsContext.Provider
      value={{
        ...defaultDataOptions,
        timeSpan,
        byUser,
        environment,
        dateFilter,
        setEnvironment,
        setDateFilter,
        updateTimeSpan,
        setByUser,
      }}
    >
      {children}
    </GlobalDataOptionsContext.Provider>
  )
}
